/**
 * Pure view-model of the Altar Forge craft screen (DOM-free, unit-tested in forgeModel.test.ts):
 * the recipe the player is building, the live odds rows, pity meters, Luck chips, the slot and
 * grade pickers, affordability ("after: 14 Jade"), the history strip and the hub badge.
 *
 * Every number comes from progression's pure `craftOdds` / `pityState` / `temperOdds`, which are
 * the same functions `craft` / `temper` roll with, so the panel can never disagree with a roll.
 */
import type { ClassId } from '@keystone/sim';
import {
  type CraftBlock,
  type CraftOdds,
  type CraftSpec,
  type ForgeHistoryEntry,
  type Item,
  type ItemSlot,
  type LuckBreakdown,
  type PityState,
  type Profile,
  type Rarity,
  type ScaleGrade,
  type SecondaryStat,
  type CraftRoll,
  type OpError,
  EYE_FRAGMENTS_PER_EYE,
  GRADE_CRAFT_ILVL,
  ITEM_SLOTS,
  MOLTSKIN_MAX_CRAFT,
  SCALE_GRADES,
  VENOM_MAX_CRAFT,
  bestCraftGrade,
  craft,
  craftCost,
  craftOdds,
  effectiveIlvl,
  equippedGear,
  favoredSlotFor,
  hasUnlock,
  itemById,
  itemOwner,
  isLocalDate,
  isTierSlot,
  legendariesFor,
  nextRunHint,
  profileRating,
  temperOdds,
  vaultPending,
  wearIfBetter,
} from '@keystone/progression';
import { RESOURCE_META, afterText, bundleText, shortfallText, scaleKey } from '../forge/resourceText';

// ---------------------------------------------------------------------------
// Recipe
// ---------------------------------------------------------------------------

/** What the player has put on the altar (UI state; becomes a CraftSpec). */
export interface ForgeRecipe {
  classId: ClassId;
  slot: ItemSlot;
  grade: ScaleGrade;
  venom: number;
  /** 0..2 stat locks (trinket: 1 = first stat, 2 = effect). */
  moltskin: number;
  stats: SecondaryStat[];
  trinketEffectId?: string;
  sigil: boolean;
  eye: boolean;
  blessed: boolean;
  /** Index into wallet.vaultTokens, or null. */
  token: number | null;
}

/** The CraftSpec of a recipe (what craft / craftOdds take). */
export function recipeSpec(r: ForgeRecipe, localDate?: string): CraftSpec {
  const spec: CraftSpec = { classId: r.classId, slot: r.slot, grade: r.grade };
  if (r.venom > 0) spec.venom = r.venom;
  if (r.moltskin > 0) {
    spec.moltskin = r.moltskin;
    spec.stats = r.stats.slice(0, 2);
    if (r.trinketEffectId) spec.trinketEffectId = r.trinketEffectId;
  }
  if (r.sigil) spec.sigil = true;
  if (r.eye) spec.eye = true;
  if (r.blessed) spec.blessed = true;
  if (r.token !== null) spec.token = r.token;
  if (localDate && isLocalDate(localDate)) spec.localDate = localDate;
  return spec;
}

/** Can this class roll a legendary in this slot (and is the Eye unlocked)? */
export function eyeEligible(p: Profile, classId: ClassId, slot: ItemSlot): boolean {
  return hasUnlock(profileRating(p), 'legendaries') && legendariesFor(classId).some((l) => l.slot === slot);
}

/**
 * Clamp a recipe to what is possible right now: reagent counts to what you hold (and the caps),
 * a Sigil only on tier slots, an Eye only where eligible, never both, a valid token. Used when
 * the player edits the recipe (the "Again" button deliberately skips it: it repeats exactly and
 * shows why it can't).
 */
export function sanitizeRecipe(p: Profile, r: ForgeRecipe): ForgeRecipe {
  const w = p.wallet;
  const out: ForgeRecipe = { ...r, stats: [...r.stats] };
  if (!p.characters[out.classId]) out.classId = p.mainClassId;
  out.venom = Math.max(0, Math.min(VENOM_MAX_CRAFT, w.venom, Math.floor(out.venom)));
  out.moltskin = Math.max(0, Math.min(MOLTSKIN_MAX_CRAFT, w.moltskin, Math.floor(out.moltskin)));
  out.stats = [...new Set(out.stats)].slice(0, 2);
  if (out.sigil && (!isTierSlot(out.slot) || w.sigils < 1)) out.sigil = false;
  if (out.eye && (!eyeEligible(p, out.classId, out.slot) || w.eyeFragments < EYE_FRAGMENTS_PER_EYE)) out.eye = false;
  if (out.eye && out.sigil) out.eye = false;
  if (out.blessed && w.blessed < 1) out.blessed = false;
  if (out.token !== null && !w.vaultTokens[out.token]) out.token = null;
  return out;
}

/** The lowest equipped item level per slot for a class (0 for an empty slot). */
function gearIlvls(p: Profile, classId: ClassId): Record<ItemSlot, number> {
  const gear = equippedGear(p, classId);
  const out = {} as Record<ItemSlot, number>;
  for (const s of ITEM_SLOTS) out[s] = gear[s] ? effectiveIlvl(gear[s]!) : 0;
  return out;
}

/** Best grade to suggest: the highest grade the wallet can pay a craft at (trade-down included). */
export function suggestedGrade(p: Profile): ScaleGrade {
  let have = 0;
  let best: ScaleGrade | null = null;
  for (const g of [...SCALE_GRADES].reverse()) {
    have += p.wallet.scales[g] ?? 0;
    if (have >= 6) {
      best = g;
      break;
    }
  }
  if (best) return best;
  // Nothing affordable: the highest grade held at all (so the shortfall reads sensibly).
  return [...SCALE_GRADES].reverse().find((g) => (p.wallet.scales[g] ?? 0) > 0) ?? 'ashen';
}

/** A sensible starting recipe: the slot a craft improves most (tier slots first with a Sigil). */
export function defaultRecipe(p: Profile, opts: { classId?: ClassId; slot?: ItemSlot; sigil?: boolean } = {}): ForgeRecipe {
  const classId = opts.classId && p.characters[opts.classId] ? opts.classId : p.mainClassId;
  const grade = suggestedGrade(p);
  let slot = opts.slot;
  if (!slot) {
    const ilvls = gearIlvls(p, classId);
    const craftIlvl = GRADE_CRAFT_ILVL[grade];
    const cap = craftOdds(p, { classId, slot: 'helm', grade }).ilvl;
    const wantTier = (opts.sigil ?? p.wallet.sigils > 0) && p.wallet.sigils > 0;
    const gear = equippedGear(p, classId);
    const score = (s: ItemSlot): number => (Math.min(craftIlvl, cap) - ilvls[s]) * 10 + (gear[s] && (gear[s]!.rarity === 'common') ? 5 : 0) + (wantTier && isTierSlot(s) && !gear[s]?.setId ? 1000 : 0);
    slot = [...ITEM_SLOTS].sort((a, b) => score(b) - score(a))[0]!;
  }
  return sanitizeRecipe(p, { classId, slot, grade, venom: 0, moltskin: 0, stats: [], sigil: !!(opts.sigil ?? true) && isTierSlot(slot), eye: false, blessed: false, token: null });
}

// ---------------------------------------------------------------------------
// Odds display
// ---------------------------------------------------------------------------

export const ODDS_RARITIES: readonly Rarity[] = ['common', 'uncommon', 'rare', 'epic', 'legendary'];

/** "35%", "12.5%", "0.5%", "0%". */
export function formatPct(n: number): string {
  const v = Math.max(0, Math.min(100, n));
  if (Number.isInteger(v)) return `${v}%`;
  const r = Math.round(v * 10) / 10;
  return `${Number.isInteger(r) ? r.toFixed(0) : r.toFixed(1)}%`;
}

export interface OddsRow {
  rarity: Rarity;
  pct: number;
  label: string;
  /** Bar width 0..1 (of 100%). */
  width: number;
  /** Change vs `prev` in percentage points (0 when no previous odds). */
  delta: number;
  /** Removed by a floor ("Rare floor"). */
  floored: boolean;
}

/**
 * One row per rarity (Legendary only for Eye crafts). `prev` (the odds before the last recipe
 * change) drives the up/down arrows as the sliders move.
 */
export function oddsRows(odds: Pick<CraftOdds, 'rarity' | 'legendary' | 'floor'>, prev?: Pick<CraftOdds, 'rarity'> | null): OddsRow[] {
  const floorIdx = odds.floor ? ODDS_RARITIES.indexOf(odds.floor) : -1;
  return ODDS_RARITIES.filter((r) => r !== 'legendary' || odds.legendary !== null).map((rarity) => {
    const pct = odds.rarity[rarity];
    const delta = prev ? Math.round((pct - prev.rarity[rarity]) * 10) / 10 : 0;
    return { rarity, pct, label: formatPct(pct), width: pct / 100, delta, floored: ODDS_RARITIES.indexOf(rarity) < floorIdx };
  });
}

/** Headline "Rare or better 55%" and "Epic or better 19%". */
export function oddsSummary(odds: Pick<CraftOdds, 'rarity'>): { rarePlus: number; epicPlus: number; rarePlusLabel: string; epicPlusLabel: string } {
  const r = odds.rarity;
  const rarePlus = r.rare + r.epic + r.legendary;
  const epicPlus = r.epic + r.legendary;
  return { rarePlus, epicPlus, rarePlusLabel: formatPct(rarePlus), epicPlusLabel: formatPct(epicPlus) };
}

export interface LuckChip {
  id: keyof Omit<LuckBreakdown, 'raw' | 'total' | 'cap'>;
  label: string;
  value: number;
}

const LUCK_LABEL: Readonly<Record<LuckChip['id'], string>> = {
  venom: 'Venom',
  blessed: 'Blessed',
  favored: 'Favored slot',
  fortune: 'Fortune craft',
  codex: 'Codex',
  token: 'Vault token',
};

/** Non-zero Luck sources as chips, plus the capped total. */
export function luckChips(l: LuckBreakdown): { chips: LuckChip[]; total: number; capped: boolean; label: string } {
  const chips = (Object.keys(LUCK_LABEL) as LuckChip['id'][]).filter((k) => l[k] > 0).map((id) => ({ id, label: LUCK_LABEL[id], value: l[id] }));
  const total = l.total;
  return { chips, total, capped: l.raw > l.cap, label: `Luck ${Number.isInteger(total) ? total : total.toFixed(1)}${l.raw > l.cap ? ` (capped at ${l.cap})` : ''}` };
}

// ---------------------------------------------------------------------------
// Pity meters
// ---------------------------------------------------------------------------

export interface PityView {
  epic: { count: number; hard: number; fill: number; soft: boolean; label: string; sub: string; guaranteedNext: boolean };
  rareFloor: { active: boolean; label: string; count: number; after: number };
  eye: { label: string; nextChance: number };
  fortune: { next: boolean; label: string; craftsUntil: number };
}

export function pityView(s: PityState): PityView {
  const e = s.epic;
  const guaranteedNext = e.guaranteedIn <= 1;
  return {
    epic: {
      count: e.count,
      hard: e.hard,
      fill: Math.max(0, Math.min(1, e.count / Math.max(1, e.hard - 1))),
      soft: e.soft,
      guaranteedNext,
      label: guaranteedNext ? 'Epic guaranteed next craft' : `Epic guaranteed in ≤ ${e.guaranteedIn}`,
      sub: e.soft ? `Soft pity: +${e.nextEpicBonus}% Epic on the next craft` : `${e.count} craft${e.count === 1 ? '' : 's'} since your last Epic`,
    },
    rareFloor: { active: s.rareFloor.next, count: s.rareFloor.count, after: s.rareFloor.after, label: s.rareFloor.next ? 'Rare+ guaranteed next' : `Rare floor in ${s.rareFloor.after - s.rareFloor.count}` },
    eye: { nextChance: s.eye.nextChance, label: s.eye.guaranteedBy <= 1 ? 'Next Eye: Legendary guaranteed' : `Next Eye ${s.eye.nextChance}% · guaranteed by Eye #${s.eye.misses + s.eye.guaranteedBy}` },
    fortune: { next: s.fortune.next, craftsUntil: s.fortune.craftsUntil, label: s.fortune.next ? 'Fortune craft! +4 Luck' : `Fortune in ${s.fortune.craftsUntil}` },
  };
}

// ---------------------------------------------------------------------------
// Blocks, affordability
// ---------------------------------------------------------------------------

const BLOCK_TEXT: Readonly<Record<Exclude<CraftBlock, 'cantAfford' | 'notEnoughGold'>, string>> = {
  unknownClass: 'Pick a hero.',
  notTierSlot: 'Sigils only work on helm, chest, legs, gloves or boots.',
  noLegendary: 'This hero has no legendary for that slot.',
  locked: 'Serpent’s Eyes unlock at Champion.',
  reagentConflict: 'A Sigil and a Serpent’s Eye can’t share a craft.',
  noToken: 'That Vault Forge Token is gone.',
  badReagent: 'Pick the stats your Moltskin locks.',
};

export interface CraftAffordability {
  ok: boolean;
  reason: string | null;
  /** "after: 14 Jade · 22 Fangsteel" (null when blocked). */
  after: string | null;
  /** "6 Jade Scales, 30 Fangsteel, 150 Gold". */
  costText: string;
}

export function craftAffordability(p: Profile, odds: CraftOdds): CraftAffordability {
  const costText = bundleText(odds.cost, 'Free');
  if (!odds.blocked) return { ok: true, reason: null, after: afterText(p, odds.cost), costText };
  const reason = odds.blocked === 'cantAfford' || odds.blocked === 'notEnoughGold' ? (shortfallText(odds.shortfall) ?? 'Not enough resources.') : BLOCK_TEXT[odds.blocked];
  return { ok: false, reason, after: null, costText };
}

// ---------------------------------------------------------------------------
// Pickers
// ---------------------------------------------------------------------------

export interface SlotOption {
  slot: ItemSlot;
  current: Item | null;
  currentIlvl: number;
  /** Item level this recipe would craft (after the tier cap). */
  craftIlvl: number;
  /** craftIlvl − current effective ilvl. */
  gain: number;
  /** A craft here is likely an upgrade: more item level, an empty slot, or a sub-Rare piece at the same level. */
  worth: boolean;
  tierSlot: boolean;
  /** The current piece is this class's tier piece. */
  hasTier: boolean;
  favored: boolean;
  legendary: boolean;
}

export function slotOptions(p: Profile, classId: ClassId, grade: ScaleGrade, localDate?: string): SlotOption[] {
  const gear = equippedGear(p, classId);
  const favored = localDate && isLocalDate(localDate) ? favoredSlotFor(localDate) : null;
  const ilvl = craftOdds(p, { classId, slot: 'helm', grade }).ilvl;
  return ITEM_SLOTS.map((slot) => {
    const current = gear[slot] ?? null;
    const currentIlvl = current ? effectiveIlvl(current) : 0;
    return {
      slot,
      current,
      currentIlvl,
      craftIlvl: ilvl,
      gain: ilvl - currentIlvl,
      worth: !current || ilvl > currentIlvl || (ilvl === currentIlvl && (current.rarity === 'common' || current.rarity === 'uncommon')),
      tierSlot: isTierSlot(slot),
      hasTier: !!current?.setId,
      favored: slot === favored,
      legendary: eyeEligible(p, classId, slot),
    };
  });
}

export interface GradeOption {
  grade: ScaleGrade;
  label: string;
  have: number;
  /** Craft ilvl after the tier cap, and before it. */
  ilvl: number;
  uncapped: number;
  capped: boolean;
  /** Enough Scales of this grade or higher for a craft. */
  enough: boolean;
}

export function gradeOptions(p: Profile, classId: ClassId, slot: ItemSlot): GradeOption[] {
  let higher = 0;
  const out: GradeOption[] = [];
  for (const grade of [...SCALE_GRADES].reverse()) {
    higher += p.wallet.scales[grade] ?? 0;
    const o = craftOdds(p, { classId, slot, grade });
    out.push({ grade, label: RESOURCE_META[scaleKey(grade)].short, have: p.wallet.scales[grade] ?? 0, ilvl: o.ilvl, uncapped: o.uncappedIlvl, capped: o.ilvl < o.uncappedIlvl, enough: higher >= 6 });
  }
  return out.reverse();
}

// ---------------------------------------------------------------------------
// History strip, "one more run", hub badge
// ---------------------------------------------------------------------------

export interface HistoryPip {
  kind: ForgeHistoryEntry['kind'];
  /** Craft rarity, or 'success' | 'surge' | 'fail'. */
  result: string;
  nearMiss: boolean;
  title: string;
}

export function historyPips(history: readonly ForgeHistoryEntry[]): HistoryPip[] {
  return history.map((h) => ({
    kind: h.kind,
    result: h.result,
    nearMiss: h.nearMiss,
    title: `${h.kind === 'craft' ? `Craft: ${h.result}` : `${h.kind[0]!.toUpperCase()}${h.kind.slice(1)}: ${h.result}`}${h.nearMiss ? ' (near miss)' : ''}`,
  }));
}

/** "Next run at +7: +8 Jade Scales ≈ 2 more temper steps". */
export function nextRunText(p: Profile, level: number): string {
  const h = nextRunHint(p, Math.max(2, level));
  const scales = h.resources.scales?.[h.grade] ?? 0;
  const parts = [`+${scales} ${RESOURCE_META[scaleKey(h.grade)].short} Scales`, `+${h.resources.fangsteel ?? 0} Fangsteel`];
  const tail = h.temperSteps > 0 ? ` ≈ ${h.temperSteps} more temper step${h.temperSteps === 1 ? '' : 's'}` : h.crafts > 0 ? ` ≈ ${h.crafts} more craft${h.crafts === 1 ? '' : 's'}` : '';
  return `Next timed +${Math.max(2, level)}: ${parts.join(', ')}${tail}`;
}

export interface ForgeAttention {
  /** A craft for the main hero that beats an equipped piece is affordable now. */
  craft: boolean;
  /** Main-hero items with an affordable temper attempt. */
  temper: number;
  /** Unspent Altar Sigils. */
  sigils: number;
  /** Unused Vault Forge Tokens. */
  tokens: number;
  vault: boolean;
  /** Number shown on the hub's Forge badge (0 = no badge). */
  badge: number;
}

/** What the hub's Forge tile badge counts: affordable crafts / tempers, Sigils and tokens. */
export function forgeAttention(p: Profile, localDate?: string): ForgeAttention {
  const main = p.mainClassId;
  const grade = suggestedGrade(p);
  const craft = slotOptions(p, main, grade, localDate).some((s) => s.worth && !craftOdds(p, { classId: main, slot: s.slot, grade }).blocked);
  let temper = 0;
  for (const it of Object.values(equippedGear(p, main))) if (it && !temperOdds(p, it, { useFree: true }).blocked) temper++;
  const sigils = p.wallet.sigils;
  const tokens = p.wallet.vaultTokens.length;
  const vault = vaultPending(p);
  return { craft, temper, sigils, tokens, vault, badge: (craft ? 1 : 0) + (temper > 0 ? 1 : 0) + sigils + tokens };
}

// ---------------------------------------------------------------------------
// Simplified craft (KMMO Forge › Craft): pick a slot, press CRAFT; the rest is automatic.
// ---------------------------------------------------------------------------

/** What the player actually chose on the simplified craft view (everything else is derived). */
export interface CraftChoice {
  slot: ItemSlot;
  /** Manual grade (tap the Scale node to cycle); undefined = automatic (bestCraftGrade). */
  grade?: ScaleGrade;
  /** Sigil node state; undefined = automatic (on for tier slots while a Sigil is held). */
  sigil?: boolean;
  /** Serpent's Eye node (off by default). */
  eye?: boolean;
  /** Venom vials (Odds panel toggle + slider). */
  venom: number;
}

const ilvlOf = (g: ScaleGrade): number => GRADE_CRAFT_ILVL[g];

/**
 * The recipe for a choice, always for the main hero: the best affordable grade under the cap
 * (`bestCraftGrade`), a held Vault Forge Token used for free when it fits that grade (or is
 * better), the Sigil on by default for tier slots, no Blessed / Moltskin (those live on Enhance).
 */
export function autoRecipe(p: Profile, c: CraftChoice): ForgeRecipe {
  const classId = p.mainClassId;
  const tokens = p.wallet.vaultTokens;
  let grade: ScaleGrade;
  if (c.grade) grade = c.grade;
  else {
    const best = bestCraftGrade(p, classId, c.slot);
    grade = best ?? tokens[0]?.grade ?? suggestedGrade(p);
    // A token at the same or a higher grade is strictly better (free, Rare floor, +Luck).
    const better = tokens.map((t) => t.grade).filter((g) => ilvlOf(g) >= ilvlOf(grade)).sort((a, b) => ilvlOf(a) - ilvlOf(b))[0];
    if (better) grade = better;
  }
  const token = tokens.findIndex((t) => t.grade === grade);
  const sigil = c.sigil ?? (isTierSlot(c.slot) && p.wallet.sigils > 0);
  return sanitizeRecipe(p, { classId, slot: c.slot, grade, venom: c.venom, moltskin: 0, stats: [], sigil, eye: !!c.eye && !sigil, blessed: false, token: token >= 0 ? token : null });
}

/** The slot the craft view opens on: the biggest gain (tier slots first while a Sigil is held). */
export function defaultCraftSlot(p: Profile, localDate?: string): ItemSlot {
  const first = defaultRecipe(p, {}).slot;
  const ok = (slot: ItemSlot): boolean => !craftOdds(p, recipeSpec(autoRecipe(p, { slot, venom: 0 }), localDate)).blocked;
  if (ok(first)) return first;
  // The suggestion can't be paid (e.g. a weapon costs more Fangsteel): the best affordable upgrade instead.
  const opts = slotOptions(p, p.mainClassId, autoRecipe(p, { slot: first, venom: 0 }).grade, localDate);
  const pick = [...opts].sort((a, b) => Number(b.worth) - Number(a.worth) || b.gain - a.gain).find((o) => ok(o.slot));
  return pick?.slot ?? first;
}

/** Next grade when the player taps the Scale node: the next payable grade (or token grade), wrapping. */
export function cycleGrade(p: Profile, slot: ItemSlot, current: ScaleGrade): ScaleGrade {
  const opts = gradeOptions(p, p.mainClassId, slot);
  const tokenGrades = new Set(p.wallet.vaultTokens.map((t) => t.grade));
  const ok = opts.filter((o) => o.enough || tokenGrades.has(o.grade)).map((o) => o.grade);
  if (!ok.length) return current;
  const i = ok.indexOf(current);
  return ok[(i + 1) % ok.length]!;
}

export interface OddsBar {
  rarity: Rarity;
  pct: number;
  label: string;
}

/** The 3 bars of the Odds panel: Rare, Epic, Legendary (Common / Uncommon are the remainder). */
export function oddsBars(odds: Pick<CraftOdds, 'rarity'>): OddsBar[] {
  return (['rare', 'epic', 'legendary'] as const).map((rarity) => ({ rarity, pct: odds.rarity[rarity], label: formatPct(odds.rarity[rarity]) }));
}

/** The single pity bar: "Epic guaranteed in N crafts" with filled segments. */
export function epicPityBar(s: PityState): { label: string; filled: number; segments: number } {
  const n = s.epic.guaranteedIn;
  return { label: n <= 1 ? 'Epic guaranteed next craft' : `Epic guaranteed in ${n} crafts`, filled: Math.min(s.epic.count, s.epic.hard - 1), segments: s.epic.hard - 1 };
}

export interface MaterialNode {
  key: 'fangsteel' | `scale.${ScaleGrade}` | 'vaultToken';
  label: string;
  have: number;
  need: number;
  short: boolean;
}

/** The (at most 2) material nodes above the craft node: Fangsteel and the Scale grade paid (or the Vault token). */
export function materialNodes(p: Profile, r: ForgeRecipe, odds: Pick<CraftOdds, 'cost'>): MaterialNode[] {
  if (r.token !== null) {
    const n = p.wallet.vaultTokens.length;
    return [{ key: 'vaultToken', label: 'Vault Token', have: n, need: 1, short: n < 1 }];
  }
  const base = craftCost(r.slot, r.grade);
  const fs = odds.cost.fangsteel ?? base.fangsteel;
  const need = Object.values(odds.cost.scales ?? {}).reduce((a, b) => a + (b ?? 0), 0) || base.scales;
  let have = 0;
  for (const g of SCALE_GRADES.slice(SCALE_GRADES.indexOf(r.grade))) have += p.wallet.scales[g] ?? 0;
  return [
    { key: 'fangsteel', label: 'Fangsteel', have: p.wallet.fangsteel, need: fs, short: p.wallet.fangsteel < fs },
    { key: scaleKey(r.grade) as MaterialNode['key'], label: RESOURCE_META[scaleKey(r.grade)].one, have, need, short: have < need },
  ];
}

/** The CRAFT button: gold cost or FREE, and why it is disabled. */
export function craftCta(p: Profile, odds: CraftOdds): { gold: number; free: boolean; disabled: boolean; reason: string | null } {
  const gold = odds.cost.gold ?? 0;
  const aff = craftAffordability(p, odds);
  return { gold, free: gold === 0 && !odds.blocked, disabled: !aff.ok, reason: aff.reason };
}

export interface CraftOutcome {
  item: Item;
  roll: CraftRoll;
  equipped: boolean;
  compareTo: Item | null;
}

/** One craft for the recipe, equipping the result when it is better ("equip if better" is always on). */
export function craftOnce(p: Profile, r: ForgeRecipe, localDate?: string): { ok: true; profile: Profile; out: CraftOutcome } | { ok: false; reason: OpError } {
  const compareTo = equippedGear(p, r.classId)[r.slot] ?? null;
  const res = craft(p, recipeSpec(r, localDate));
  if (!res.ok) return { ok: false, reason: res.reason };
  const worn = wearIfBetter(res.profile, r.classId, res.item.id);
  const equipped = itemOwner(worn, res.item.id) === r.classId;
  return { ok: true, profile: worn, out: { item: itemById(worn, res.item.id) ?? res.item, roll: res.roll, equipped, compareTo } };
}

/**
 * "Craft x5": repeat a plain craft (never spends a Sigil or an Eye) up to `n` times; stops on
 * Epic or better, or when the next one can't be paid.
 */
export function craftBatch(p: Profile, choice: CraftChoice, n: number, localDate?: string): { profile: Profile; results: CraftOutcome[]; stopped: 'epic' | 'blocked' | null } {
  let cur = p;
  const results: CraftOutcome[] = [];
  for (let i = 0; i < n; i++) {
    const r = autoRecipe(cur, { ...choice, sigil: false, eye: false });
    if (craftOdds(cur, recipeSpec(r, localDate)).blocked) return { profile: cur, results, stopped: 'blocked' };
    const res = craftOnce(cur, r, localDate);
    if (!res.ok) return { profile: cur, results, stopped: 'blocked' };
    cur = res.profile;
    results.push(res.out);
    if (res.out.item.rarity === 'epic' || res.out.item.rarity === 'legendary') return { profile: cur, results, stopped: 'epic' };
  }
  return { profile: cur, results, stopped: null };
}

// ---------------------------------------------------------------------------
// Forge rail
// ---------------------------------------------------------------------------

export type ForgeTab = 'enhance' | 'craft' | 'sets';
export const FORGE_TABS: readonly ForgeTab[] = ['enhance', 'craft', 'sets'];

/**
 * Which rail tab a route opens on: `params.tab` wins; the legacy route ids map onto tabs
 * (temper → Enhance, sets → Sets, forge / codex → Craft) so old hotkeys and links keep working.
 */
export function forgeTabFor(routeId: string, params: Readonly<Record<string, unknown>>): ForgeTab {
  const t = params['tab'];
  if (typeof t === 'string' && (FORGE_TABS as readonly string[]).includes(t)) return t as ForgeTab;
  if (routeId === 'temper') return 'enhance';
  if (routeId === 'sets') return 'sets';
  if (typeof params['itemId'] === 'string' && typeof params['slot'] !== 'string') return 'enhance';
  return 'craft';
}
